import { useEffect, useRef, useState, useCallback } from "react";
import PcmAudioRecorder from "../Helpers/realtimeApi/PcmAudioRecorder";
import RealtimeTranscriptionClient from "../Helpers/realtimeApi/RealtimeTranscriptionClient";
import { useTranslate } from "@tolgee/react";

export const useRealtimeTranscription = ({ language, onFinalText }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);
  const { t } = useTranslate();

  const clientRef = useRef(null);
  const recorderRef = useRef(null);
  const micStreamRef = useRef(null);
  const isStartingRef = useRef(false);
  const onFinalTextRef = useRef(onFinalText);

  useEffect(() => {
    onFinalTextRef.current = onFinalText;
  }, [onFinalText]);

  const stopListening = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((track) => track.stop());
      micStreamRef.current = null;
    }
    if (clientRef.current) {
      clientRef.current.disconnect();
    }
    setIsListening(false);
  }, []);

  const ensureClient = useCallback(() => {
    if (!clientRef.current) {
      clientRef.current = new RealtimeTranscriptionClient({
        onOpen: () => setIsConnected(true),
        onClose: () => {
          setIsConnected(false);
          stopListening();
        },
        onError: (err) => {
          console.error("Transcription WebSocket Error:", err);
          setError(t("translatePage.mic_error"));
        },
        onRecognizedText: (payload) => {
          if (onFinalTextRef.current) onFinalTextRef.current(payload.text);
        },
      });
    }
    return clientRef.current;
  }, [stopListening, t]);

  const startListening = async () => {
    if (isStartingRef.current || isListening) return;
    isStartingRef.current = true;
    setError(null);

    try {
      if (micStreamRef.current) stopListening();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const client = ensureClient();
      await client.connect({ language });

      recorderRef.current = new PcmAudioRecorder();
      recorderRef.current.start({
        mediaStreamTrack: stream.getAudioTracks()[0],
        onAudio: (pcmChunk) => {
          if (client.isConnected) {
            client.sendAudio(pcmChunk);
          }
        },
      });

      setIsListening(true);
    } catch (err) {
      console.error("Failed to start listening:", err);
      setError(t("translatePage.mic_error"));
      setIsListening(false);
    } finally {
      isStartingRef.current = false;
    }
  };

  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  return {
    isConnected,
    isListening,
    startListening,
    stopListening,
    error,
  };
};
